import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import type { PRPortalRecord } from '@/types/browserAutomation';

interface SearchResultsTableProps {
  records: PRPortalRecord[];
}

const RecordRow = ({ record }: { record: PRPortalRecord }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer border-b border-gray-100"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-3 py-2 text-xs">
          {expanded ? (
            <ChevronDownIcon className="h-3 w-3 text-gray-400" />
          ) : (
            <ChevronRightIcon className="h-3 w-3 text-gray-400" />
          )}
        </td>
        <td className="px-3 py-2 text-xs font-medium text-gray-900">
          {record.report_number || record.portal_record_id || '—'}
        </td>
        <td className="px-3 py-2 text-xs text-gray-700">
          {record.date_of_incident
            ? new Date(record.date_of_incident).toLocaleDateString()
            : '—'}
        </td>
        <td className="px-3 py-2 text-xs text-gray-700">
          {record.department_name || '—'}
        </td>
        <td className="px-3 py-2 text-xs text-gray-700 capitalize">
          {record.availability?.replace(/_/g, ' ') || '—'}
        </td>
        <td className="px-3 py-2 text-xs text-gray-900 font-medium text-right">
          {record.request_cost_cents !== null &&
          record.request_cost_cents !== undefined
            ? `$${(record.request_cost_cents / 100).toFixed(2)}`
            : '—'}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-blue-50/30">
          <td colSpan={6} className="px-3 py-3">
            <div className="ml-6 grid grid-cols-3 gap-x-4 gap-y-2 text-xs">
              {record.portal_record_id && (
                <div>
                  <div className="text-gray-500 font-medium">Portal ID</div>
                  <div className="font-mono text-gray-900">
                    {record.portal_record_id}
                  </div>
                </div>
              )}
              {record.portal_agency_id && (
                <div>
                  <div className="text-gray-500 font-medium">Agency ID</div>
                  <div className="text-gray-900">{record.portal_agency_id}</div>
                </div>
              )}
              {record.state_report_number && (
                <div>
                  <div className="text-gray-500 font-medium">
                    State Report #
                  </div>
                  <div className="text-gray-900">
                    {record.state_report_number}
                  </div>
                </div>
              )}
              {record.incident_location && (
                <div className="col-span-3">
                  <div className="text-gray-500 font-medium">Location</div>
                  <div className="text-gray-900">
                    {record.incident_location}
                  </div>
                </div>
              )}
              {record.incident_street && (
                <div className="col-span-2">
                  <div className="text-gray-500 font-medium">Street</div>
                  <div className="text-gray-900">{record.incident_street}</div>
                </div>
              )}
              {record.names && record.names.length > 0 && (
                <div className="col-span-3">
                  <div className="text-gray-500 font-medium">Names</div>
                  <div className="text-gray-900">
                    {record.names.filter(Boolean).join(', ')}
                  </div>
                </div>
              )}
              {record.purchased !== null && record.purchased !== undefined && (
                <div>
                  <div className="text-gray-500 font-medium">Purchased</div>
                  <div className="text-gray-900">
                    {record.purchased ? 'Yes' : 'No'}
                  </div>
                </div>
              )}
              {record.s3_url && (
                <div className="col-span-3">
                  <div className="text-gray-500 font-medium">S3 URL</div>
                  <div className="text-gray-900 font-mono text-xs break-all">
                    {record.s3_url}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export const SearchResultsTable = ({ records }: SearchResultsTableProps) => {
  if (records.length === 0) {
    return (
      <div className="text-sm text-gray-500 text-center py-4">
        No records found
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="w-8" />
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
              Report #
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
              Date
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
              Department
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
              Availability
            </th>
            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">
              Cost
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {records.map((record, idx) => (
            <RecordRow key={idx} record={record} />
          ))}
        </tbody>
      </table>
    </div>
  );
};
